import {
  setCollections,
  useCollectionsStore,
  setSelectedRequest,
  setEditingItem,
  setSelectedCollection,
  setSelectedFolder,
} from '../../store/collectionsStore';
import {
  setRequestTabs,
  useTabsStore,
  setCollectionTabs,
  setActiveCollectionTabId,
  setActiveTabType,
} from '../../store/tabsStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { useAuthStore } from '../../store/authStore';
import { setShowCollectionModal, setUpgradeReason, setUpgradeModalOpen } from '../../store/uiModalStore';
import { requireWritableWorkspace } from '../../plans/localGate';
import { requireApiCapacity } from '../../plans/localApiUsage';
import { collectionService } from '../../services/collectionService';
import { apiRequestService } from '../../services/apiRequestService';
import { isCloudRestWorkspace } from '../../utils/workspaceStorage';
import { createFolderInCollection, deleteFolderFromCollection, collectFolderSubtreeIds } from '../../utils/collectionFolders';
import { markLocalOnly } from '../../services/localCollectionLink';
import { closeFolderTabs } from '../../utils/folderTabs';
import { blankRequestOfKind } from '../../utils/requestKinds';

/** Create and delete collections, requests and folders; collection settings and reorder. */
export function useCollectionCrud({
  toastT,
  createNewRequestTab,
  updateCurrentRequestTabStable,
  scheduleTreeSave,
}) {
  // ---- Collection / request / folder CRUD ----------------------------------
  const handleCreateCollection = async (name) => {
    const ws = useWorkspaceStore.getState().currentWorkspace;
    const authed = useAuthStore.getState().isAuthenticated;
    // Plans v2: an own cloud workspace is read-only without cloud_sync.
    if (!requireWritableWorkspace(ws)) return null;
    try {
      if (isCloudRestWorkspace(ws, authed)) {
        const newCollection = await collectionService.createCollection({
          name,
          workspace_id: ws.id,
        });
        setCollections((prev) => [...prev, newCollection]);
        setShowCollectionModal(false);
        toastT('toast.collectionCreated', 'success', { name });
        return newCollection;
      } else if (authed && !ws) {
        // Avoid silently creating local collections when authenticated state is still loading.
        toastT('toast.noWorkspaceForCollection', 'warning');
      } else {
        // Explicit local-only mode for guests (no backend request).
        // Plans v2: Free makes 2 collections (across its local workspaces).
        if (!(await requireApiCapacity('collections'))) return null;
        const newCollection = markLocalOnly({
          id: Date.now().toString(),
          name,
          requests: [],
        });
        setCollections((prev) => [...prev, newCollection]);
        setShowCollectionModal(false);
        toastT('toast.collectionCreatedLocal', 'info', { name });
        return newCollection;
      }
    } catch (error) {
      if (error.response?.data?.upgrade_required) {
        setUpgradeReason(error.response.data.error || '');
        setUpgradeModalOpen(true);
        return;
      }
      console.error('Failed to create collection:', error);
      toastT('toast.createCollectionFailed', 'error');
    }
    return null;
  };

  // `kind`: 'websocket' | 'sse' | 'grpc' from the sidebar's menu; default plain HTTP.
  const createRequest = (collectionId, folderId = null, kind = 'http') => {
    // Create a new request tab immediately without modal
    createNewRequestTab(blankRequestOfKind(kind, { collectionId, folderId: folderId || null }));

    // Mark the tab as having unsaved changes since it's a new request
    updateCurrentRequestTabStable({ hasUnsavedChanges: true });
  };

  /**
   * @param {Object}  request
   * @param {Object}  [opts]
   * @param {boolean} [opts.silent] suppress the per-item toast — the BULK path
   *   sets this and emits one summary toast instead, because fifty deletes would
   *   otherwise be fifty toasts. Default `false`, so every existing caller keeps
   *   today's message unchanged.
   * @returns {Promise<boolean>} whether the delete succeeded (for the bulk tally)
   */
  const deleteRequest = async (request, { silent = false } = {}) => {
    const ws = useWorkspaceStore.getState().currentWorkspace;
    const authed = useAuthStore.getState().isAuthenticated;
    try {
      if (isCloudRestWorkspace(ws, authed) && request.id) {
        await apiRequestService.deleteRequest(request.id);
      }

      setCollections((prev) =>
        prev.map((collection) => ({
          ...collection,
          requests: (collection.requests || []).filter((r) => r.id !== request.id),
        }))
      );

      setRequestTabs((prev) =>
        prev.filter((tab) => tab.originalId !== request.id && tab.id !== request.id)
      );

      const { selectedRequest, editingItem } = useCollectionsStore.getState();
      if (selectedRequest?.id === request.id) {
        setSelectedRequest(null);
      }
      if (editingItem?.type === 'request' && String(editingItem.id) === String(request.id)) {
        setEditingItem(null);
      }

      if (!silent) toastT('toast.requestDeleted', 'success', { name: request.name });
      return true;
    } catch (error) {
      console.error('Failed to delete request:', error);
      if (!silent) toastT('toast.deleteRequestFailed', 'error', { message: error.message });
      return false;
    }
  };

  /** @see deleteRequest for `opts.silent` and the boolean return. */
  const deleteCollection = async (collection, { silent = false } = {}) => {
    const ws = useWorkspaceStore.getState().currentWorkspace;
    const authed = useAuthStore.getState().isAuthenticated;
    try {
      if (isCloudRestWorkspace(ws, authed) && collection.id) {
        await collectionService.deleteCollection(collection.id);
      }

      setCollections((prev) => prev.filter((c) => c.id !== collection.id));

      const { collectionTabs, activeCollectionTabId } = useTabsStore.getState();
      const collections = useCollectionsStore.getState().collections;
      const remainingCollectionTabs = collectionTabs.filter(
        (tab) => String(tab.collectionId) !== String(collection.id)
      );
      const closedActiveCollectionTab = collectionTabs.some(
        (tab) =>
          tab.id === activeCollectionTabId &&
          String(tab.collectionId) === String(collection.id)
      );
      setCollectionTabs(remainingCollectionTabs);
      if (closedActiveCollectionTab) {
        if (remainingCollectionTabs.length > 0) {
          const next = remainingCollectionTabs[remainingCollectionTabs.length - 1];
          setActiveCollectionTabId(next.id);
          setActiveTabType('collection');
          const col = collections.find((c) => String(c.id) === String(next.collectionId));
          setSelectedCollection(col || null);
        } else {
          setActiveCollectionTabId(null);
          setActiveTabType('request');
        }
      }

      const { selectedCollection, selectedFolder, editingItem } = useCollectionsStore.getState();
      if (selectedCollection?.id === collection.id) {
        setSelectedCollection(null);
      }
      if (selectedFolder && String(selectedFolder.collectionId) === String(collection.id)) {
        setSelectedFolder(null);
      }
      if (editingItem?.type === 'collection' && String(editingItem.id) === String(collection.id)) {
        setEditingItem(null);
      }

      if (!silent) toastT('toast.collectionDeleted', 'success', { name: collection.name });
      return true;
    } catch (error) {
      console.error('Failed to delete collection:', error);
      if (!silent) toastT('toast.deleteCollectionFailed', 'error', { message: error.message });
      return false;
    }
  };

  const persistCollectionSettings = async (collectionId, settings) => {
    const ws = useWorkspaceStore.getState().currentWorkspace;
    const authed = useAuthStore.getState().isAuthenticated;
    if (isCloudRestWorkspace(ws, authed) && ws) {
      await collectionService.updateCollection(collectionId, { settings });
    }
  };

  const handleCreateFolder = (collectionId, parentFolderId = null) => {
    if (!requireWritableWorkspace()) return;
    const collections = useCollectionsStore.getState().collections;
    const collection = collections.find((c) => String(c.id) === String(collectionId));
    if (!collection) return;
    const next = createFolderInCollection(collection, { name: 'New Folder', parent_id: parentFolderId });
    const nextCollections = collections.map((c) =>
      String(c.id) === String(collectionId) ? next : c
    );
    setCollections(nextCollections);
    scheduleTreeSave(nextCollections);
    toastT('toast.folderCreated', 'success');
  };

  /** Create a named folder and return its generated id (for the destination picker). */
  const createFolderReturningId = (collectionId, parentFolderId = null, name = 'New Folder') => {
    if (!requireWritableWorkspace()) return null;
    const collections = useCollectionsStore.getState().collections;
    const collection = collections.find((c) => String(c.id) === String(collectionId));
    if (!collection) return null;
    const beforeIds = new Set((collection.settings?.folders || []).map((f) => String(f.id)));
    const next = createFolderInCollection(collection, { name: name || 'New Folder', parent_id: parentFolderId });
    const newFolder = (next.settings?.folders || []).find((f) => !beforeIds.has(String(f.id)));
    const nextCollections = collections.map((c) =>
      String(c.id) === String(collectionId) ? next : c
    );
    setCollections(nextCollections);
    scheduleTreeSave(nextCollections);
    return newFolder ? newFolder.id : null;
  };

  /** @see deleteRequest for `opts.silent` and the boolean return. */
  const handleDeleteFolder = async (collectionId, folder, { silent = false } = {}) => {
    const collections = useCollectionsStore.getState().collections;
    const ws = useWorkspaceStore.getState().currentWorkspace;
    const authed = useAuthStore.getState().isAuthenticated;
    const collection = collections.find((c) => String(c.id) === String(collectionId));
    if (!collection) return false;
    const subtreeIds = collectFolderSubtreeIds(collection.settings?.folders || [], folder.id);
    const toDeleteRequests = (collection.requests || []).filter((r) =>
      subtreeIds.has(String(r.folder_id))
    );
    const next = deleteFolderFromCollection(collection, folder.id);
    const nextCollections = collections.map((c) =>
      String(c.id) === String(collectionId) ? next : c
    );
    setCollections(nextCollections);
    const selectedFolder = useCollectionsStore.getState().selectedFolder;
    if (
      selectedFolder &&
      String(selectedFolder.id) === String(folder.id) &&
      String(selectedFolder.collectionId) === String(collectionId)
    ) {
      setSelectedFolder(null);
    }
    closeFolderTabs(collectionId, subtreeIds);
    let anyFailed = false;
    if (isCloudRestWorkspace(ws, authed)) {
      // Requests are real DB rows — delete them, then persist the new tree (folders +
      // remaining request order) in one batched call.
      for (const req of toDeleteRequests) {
        try {
          await apiRequestService.deleteRequest(req.id);
        } catch (e) {
          console.error('Failed to delete request in folder:', e);
          anyFailed = true;
        }
      }
      scheduleTreeSave(nextCollections);
    }
    if (!silent) toastT('toast.folderDeleted', 'success', { name: folder.name });
    return !anyFailed;
  };

  const handleSidebarCollectionsReorder = (nextCollections) => {
    setCollections(nextCollections);

    setRequestTabs((prevTabs) =>
      prevTabs.map((tab) => {
        if (tab.originalId == null) return tab;
        const parent = nextCollections.find((c) =>
          (c.requests || []).some((r) => String(r.id) === String(tab.originalId))
        );
        if (!parent) return tab;
        if (String(tab.collectionId) !== String(parent.id)) {
          return { ...tab, collectionId: parent.id };
        }
        return tab;
      })
    );

    scheduleTreeSave(nextCollections);
  };

  return {
    handleCreateCollection,
    createRequest,
    deleteRequest,
    deleteCollection,
    persistCollectionSettings,
    handleCreateFolder,
    createFolderReturningId,
    handleDeleteFolder,
    handleSidebarCollectionsReorder,
  };
}
